/**
 * Siembra por rol (spec 2026-10-06-permisos-empresa-carpetas, Fase 0.2).
 *
 * Al asignar una empresa, los permisos del usuario en ella ARRANCAN con los de
 * su rol, y en la empresa se pueden reducir (el rol deja de ser piso):
 *
 *   | Quién                                      | Matriz que se siembra         |
 *   | root (documents.root === true)             | ninguna                       |
 *   | personal global (level 'all', sin alcance) | ninguna: su rol es el defecto |
 *   | admin de empresa (Usuarios[].isAdmin)      | ADMIN_MATRIX                  |
 *   | con alcance «Solo sus empresas»            | permisos del rol, en granular |
 *   | cliente (roleId 'cliente' o 'usuario')     | plantilla de cliente          |
 *   | cualquier otro rol (p. ej. auxiliar)       | permisos del rol, en granular |
 *
 * Precedencia: root > personal global > admin de empresa > con alcance >
 * cliente > rol. `role` llega YA pasado por applyUserScope (lib/user_scope.js):
 * con alcance trae level 'company' y scoped:true. `roleId` (por defecto
 * role.id) identifica el rol: la clase 'client' es SOLO para CLIENT_ROLE_IDS;
 * sin roleId se cae en 'role' (permisos del rol), nunca en la plantilla.
 *
 * COPIA en daysi-cloud-functions/scripts/role_seed_lib.js; role_seed.test.js
 * exige que den lo mismo. Si cambias uno, cambia el otro. Módulo puro.
 */
import { GRANULAR_DOC_FLAGS, toGranular } from './permission_granular';
import { clientMatrixPermissions } from './client_template';

export const FOLDER_KEYS = ['read', 'create', 'update', 'delete', 'closing', 'download'];

// Únicos roles que se tratan como cliente (plantilla de cliente).
export const CLIENT_ROLE_IDS = ['cliente', 'usuario'];

export const ADMIN_MATRIX = {
  documents: {
    granular: true,
    read: true,
    create: true,
    createXml: true,
    createLink: true,
    createReference: true,
    update: true,
    disapprove: true,
    delete: true,
    deleteXml: true,
    deleteReference: true,
    deleteLinked: false,
    accounting: true,
    deleteAccounting: false,
    download: true,
  },
  folders: { read: true, create: true, update: true, delete: true, closing: false, download: true },
};

const copy = (m) => ({ documents: { ...m.documents }, folders: { ...m.folders } });

export const userKind = ({ role, isAdmin, roleId = role?.id } = {}) => {
  const d = (role && role.documents) || {};
  if (d.root === true) return 'root';
  if (d.level === 'all') return 'staff';
  if (isAdmin === true) return 'admin';
  if (d.scoped === true) return 'scoped';
  if (CLIENT_ROLE_IDS.includes(roleId)) return 'client';
  return 'role';
};

export const rolePermsGranular = (role) => {
  const g = toGranular({ documents: (role && role.documents) || {} }).documents;
  const documents = { granular: true };
  GRANULAR_DOC_FLAGS.forEach((f) => { documents[f] = !!g[f]; });
  const src = (role && role.folders) || {};
  const folders = {};
  FOLDER_KEYS.forEach((k) => { folders[k] = k === 'closing' ? false : !!src[k]; });
  return { documents, folders };
};

export const seedMatrixFor = ({ role, isAdmin, roleId = role?.id } = {}) => {
  switch (userKind({ role, isAdmin, roleId })) {
    case 'root':
    case 'staff':
      return null;
    case 'admin':
      return copy(ADMIN_MATRIX);
    case 'client':
      return clientMatrixPermissions();
    default: // 'scoped' y 'role'
      return rolePermsGranular(role);
  }
};

export const defaultCompanyPerms = ({ role, isAdmin, roleId = role?.id } = {}) => {
  const kind = userKind({ role, isAdmin, roleId });
  if (kind === 'root') return null;
  if (kind === 'staff') return rolePermsGranular(role);
  return seedMatrixFor({ role, isAdmin, roleId });
};

export default seedMatrixFor;
